/**
 * Section Detection Utilities (INTERMEDIATE)
 * 
 * ⚠️ TEMPORARY IMPLEMENTATION
 * This is a client-side workaround for detecting disconnected sections.
 * 
 * TODO: Replace with proper backend section detection in export_offset_map.py
 * See: MULTI_SECTION_SHOPS_PLAN.md Phase 1
 * 
 * Once Python exports section metadata in offset_map.json, this file can be
 * simplified to just parse the sections array instead of detecting them.
 */

import type { OffsetMap } from '@/types/offsetMap'

export interface Section {
  sectionId: number
  startFrame: number
  endFrame: number
  frameCount: number
  frames: number[]
}

/**
 * INTERMEDIATE: Detect disconnected sections by finding gaps in sequential offsets
 * 
 * This is a client-side workaround until Python exports proper section metadata.
 * 
 * Algorithm:
 * - Walk through frames 0 to N-1
 * - When offset[i-1 -> i] is missing, that's a section boundary
 * - Create new section starting at frame i
 * 
 * @param offsetMap - Offset map with all_pairs
 * @param totalFrames - Total number of frames
 * @returns Array of sections with frame ranges
 */
export function detectSectionsFromOffsets(
  offsetMap: OffsetMap,
  totalFrames: number
): Section[] {
  const sections: Section[] = []
  let currentSectionStart = 0
  let currentSectionFrames: number[] = [0]
  
  for (let i = 1; i < totalFrames; i++) {
    const offsetKey = `${i - 1}_${i}`
    const hasOffset = offsetMap.all_pairs?.[offsetKey] !== undefined
    
    if (!hasOffset) {
      // Gap detected! End current section and start new one
      sections.push({
        sectionId: sections.length,
        startFrame: currentSectionStart,
        endFrame: i - 1,
        frameCount: currentSectionFrames.length,
        frames: currentSectionFrames
      })
      
      // Start new section
      currentSectionStart = i
      currentSectionFrames = [i]
    } else {
      // Continue current section
      currentSectionFrames.push(i)
    }
  }
  
  // Add final section
  if (currentSectionFrames.length > 0) {
    sections.push({
      sectionId: sections.length,
      startFrame: currentSectionStart,
      endFrame: totalFrames - 1,
      frameCount: currentSectionFrames.length,
      frames: currentSectionFrames
    })
  }
  
  console.log(`🔍 [SectionDetection] INTERMEDIATE: Detected ${sections.length} sections from gap detection`)
  console.log(`📊 Total frames analyzed: ${totalFrames}`)
  console.log(`📊 All pairs in offset_map: ${Object.keys(offsetMap.all_pairs || {}).length}`)
  sections.forEach(s => {
    console.log(`  📌 Section ${s.sectionId}: frames ${s.startFrame}-${s.endFrame} (${s.frameCount} frames)`)
  })
  
  // Log which consecutive pairs are MISSING (these create boundaries)
  console.log(`🔗 [SectionDetection] Gap analysis:`)
  let gapCount = 0
  for (let i = 1; i < totalFrames; i++) {
    const offsetKey = `${i - 1}_${i}`
    const hasOffset = offsetMap.all_pairs?.[offsetKey] !== undefined
    if (!hasOffset) {
      console.log(`  ❌ Missing offset: ${offsetKey} (boundary between section)`)
      gapCount++
    }
  }
  console.log(`  Total gaps found: ${gapCount}`)
  
  return sections
}

/**
 * INTERMEDIATE: Find which section a frame belongs to
 * 
 * @param frameIndex - Frame index to check
 * @param sections - Array of sections
 * @returns Section ID or -1 if not found
 */
export function getFrameSection(frameIndex: number, sections: Section[]): number {
  for (const section of sections) {
    if (section.frames.includes(frameIndex)) {
      return section.sectionId
    }
  }
  return -1
}

/**
 * INTERMEDIATE: Find representative frame for a section
 * 
 * Simple heuristic: pick the middle frame
 * 
 * TODO: Replace with Python calculation based on:
 * - Most connections
 * - Most observed 3D points
 * - Best visual quality
 * 
 * @param section - Section to analyze
 * @returns Frame index to use as representative
 */
export function getRepresentativeFrame(section: Section): number {
  // Simple: pick middle frame
  const middleIdx = Math.floor(section.frames.length / 2)
  return section.frames[middleIdx]
}
